import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Plus, Edit, Trash2, ArrowLeft, Star, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

const EMPTY = { author_name: '', rating: 5, text: '', location: '', source: 'manual', is_visible: true, sort_order: 0 };

export const AdminReviews = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  useEffect(() => {
    if (authLoading) return;
    if (!user || user.role !== 'admin') { navigate('/login'); return; }
    fetchReviews();
  }, [user, authLoading, navigate]);

  const fetchReviews = async () => {
    try {
      const res = await api.get('/admin/reviews');
      setReviews(res.data);
    } catch (e) {
      toast.error('Failed to load reviews');
    } finally {
      setLoading(false);
    }
  };

  const openAdd = () => {
    setEditing(null);
    setForm({ ...EMPTY, sort_order: reviews.length });
    setShowDialog(true);
  };

  const openEdit = (r) => {
    setEditing(r);
    setForm({
      author_name: r.author_name,
      rating: r.rating,
      text: r.text,
      location: r.location || '',
      source: r.source || 'manual',
      is_visible: !!r.is_visible,
      sort_order: r.sort_order || 0,
    });
    setShowDialog(true);
  };

  const handleSubmit = async () => {
    if (!form.author_name || !form.text) { toast.error('Name and text are required'); return; }
    try {
      if (editing) {
        await api.put(`/admin/reviews/${editing.id}`, form);
        toast.success('Review updated');
      } else {
        await api.post('/admin/reviews', form);
        toast.success('Review added');
      }
      setShowDialog(false);
      fetchReviews();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const toggleVisible = async (r) => {
    try {
      await api.put(`/admin/reviews/${r.id}`, { is_visible: !r.is_visible });
      fetchReviews();
    } catch (e) {
      toast.error('Update failed');
    }
  };

  const handleDelete = async (r) => {
    if (!window.confirm(`Delete review by "${r.author_name}"?`)) return;
    try {
      await api.delete(`/admin/reviews/${r.id}`);
      toast.success('Deleted');
      fetchReviews();
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  if (authLoading || !user || user.role !== 'admin') return null;

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Link to="/admin" className="text-orange-700 hover:text-orange-900" aria-label="Back to dashboard">
            <ArrowLeft size={20} />
          </Link>
          <h1 className="text-3xl font-bold text-orange-800">Customer Reviews</h1>
        </div>
        <Button
          data-testid="add-review-btn"
          onClick={openAdd}
          className="bg-orange-600 hover:bg-orange-700"
        >
          <Plus size={16} className="mr-1" /> Add Review
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-600"></div>
        </div>
      ) : reviews.length === 0 ? (
        <div className="bg-white rounded-xl border p-12 text-center text-gray-500">
          No reviews yet. Click <strong>Add Review</strong> to create your first one.
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {reviews.map((r) => (
            <div
              key={r.id}
              data-testid={`admin-review-${r.id}`}
              className={`bg-white rounded-xl border p-5 ${r.is_visible ? '' : 'opacity-60'}`}
            >
              <div className="flex items-start justify-between mb-2">
                <div>
                  <p className="font-semibold text-gray-900">{r.author_name}</p>
                  <p className="text-xs text-gray-500">{r.location}</p>
                </div>
                <div className="flex">
                  {[1,2,3,4,5].map(i => (
                    <Star key={i} size={14} className={i <= r.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'} />
                  ))}
                </div>
              </div>
              <p className="text-sm text-gray-700 line-clamp-4 mb-4">{r.text}</p>
              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-400">Order: {r.sort_order}</span>
                <div className="flex gap-1">
                  <button
                    onClick={() => toggleVisible(r)}
                    className="p-2 hover:bg-gray-100 rounded"
                    title={r.is_visible ? 'Hide from site' : 'Show on site'}
                  >
                    {r.is_visible ? <Eye size={14} /> : <EyeOff size={14} />}
                  </button>
                  <button onClick={() => openEdit(r)} className="p-2 hover:bg-gray-100 rounded">
                    <Edit size={14} />
                  </button>
                  <button onClick={() => handleDelete(r)} className="p-2 hover:bg-red-50 text-red-600 rounded">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Review' : 'Add Review'}</DialogTitle>
            <DialogDescription>
              {editing
                ? 'Update the testimonial details. Changes appear on the homepage immediately.'
                : 'Add a new customer testimonial that will appear on the homepage.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Author Name *</Label>
              <Input value={form.author_name} onChange={e => setForm({...form, author_name: e.target.value})} />
            </div>
            <div>
              <Label>Location (e.g. Howrah, WB)</Label>
              <Input value={form.location} onChange={e => setForm({...form, location: e.target.value})} />
            </div>
            <div>
              <Label>Rating (1-5) *</Label>
              <Input type="number" min="1" max="5" value={form.rating} onChange={e => setForm({...form, rating: parseInt(e.target.value || '5', 10)})} />
            </div>
            <div>
              <Label>Review Text *</Label>
              <textarea
                className="w-full border rounded-md p-2 min-h-[100px]"
                value={form.text}
                onChange={e => setForm({...form, text: e.target.value})}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Sort Order (lower = first)</Label>
                <Input type="number" value={form.sort_order} onChange={e => setForm({...form, sort_order: parseInt(e.target.value || '0', 10)})} />
              </div>
              <div>
                <Label>Source</Label>
                <select
                  className="w-full border rounded-md p-2 h-10"
                  value={form.source}
                  onChange={e => setForm({...form, source: e.target.value})}
                >
                  <option value="manual">Manual / Testimonial</option>
                  <option value="google">Google Review</option>
                </select>
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.is_visible}
                onChange={e => setForm({...form, is_visible: e.target.checked})}
              />
              Visible on homepage
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSubmit} className="bg-orange-600 hover:bg-orange-700">
              {editing ? 'Update' : 'Add'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
